import { ReactNode } from 'react';
import { LucideIcon, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Badge } from '@/core/components/ui/badge';
import { copyToClipboard } from '@/core/utils/clipboard';

export type FieldType = 'text' | 'number' | 'currency' | 'date' | 'id' | 'status' | 'email' | 'phone';

interface InfoFieldProps {
  label: string;
  value: string | number | null | undefined;
  type?: FieldType;
  icon?: LucideIcon;
  copyable?: boolean;
  linkable?: boolean;
  onLinkClick?: () => void;
  currency?: string;
  statusType?: 'success' | 'warning' | 'error' | 'neutral';
  className?: string;
  children?: ReactNode;
}

export function InfoField({
  label,
  value,
  type = 'text',
  icon: Icon,
  copyable = false,
  linkable = false,
  onLinkClick,
  currency = 'ILS',
  statusType = 'neutral',
  className = '',
  children
}: InfoFieldProps) {
  const formatValue = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined || val === '') return 'N/A';

    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
        }).format(Number(val));

      case 'number':
        return Number(val).toLocaleString();

      case 'date':
        // Format ISO date strings or Date objects to human-readable format
        try {
          const date = val instanceof Date ? val : new Date(val);
          if (isNaN(date.getTime())) return String(val);
          return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
        } catch {
          return String(val);
        }

      case 'id':
      case 'phone':
      case 'email':
        return String(val);

      default:
        return String(val);
    }
  };

  const getValueClassName = (): string => {
    const baseClasses = 'text-sm font-medium mt-1';
    
    switch (type) {
      case 'id':
        return `${baseClasses} font-mono text-gray-900`;
      case 'currency':
      case 'number':
        return `${baseClasses} text-gray-900 font-semibold`;
      case 'email':
      case 'phone':
        return `${baseClasses} text-blue-600 hover:text-blue-800`;
      default:
        return `${baseClasses} text-gray-900`;
    }
  };

  const handleCopy = async () => {
    if (value) {
      await copyToClipboard(String(value), 'Copied');
    }
  };

  const handleLinkClick = () => {
    if (linkable && onLinkClick) {
      onLinkClick();
    }
  };

  const renderValue = () => {
    if (children) {
      return children;
    }

    if (type === 'status' && typeof value === 'string') {
      const badgeVariant = statusType === 'success' ? 'default' : 
                          statusType === 'error' ? 'destructive' : 
                          statusType === 'warning' ? 'secondary' : 'outline';
      
      return (
        <Badge variant={badgeVariant} className="mt-1">
          {value}
        </Badge>
      );
    }

    const formattedValue = formatValue(value);
    const valueClasses = getValueClassName();
    
    if (linkable) {
      return (
        <button
          onClick={handleLinkClick}
          className={`${valueClasses} hover:underline cursor-pointer flex items-center gap-1`}
        >
          {formattedValue}
          <ExternalLink className="h-3 w-3" />
        </button>
      );
    }

    return (
      <p className={valueClasses}>
        {formattedValue}
      </p>
    );
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wide flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </label>
      
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {renderValue()}
        </div>
        
        {copyable && value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-6 w-6 p-0 hover:bg-gray-100 rounded flex-shrink-0"
            title="Copy to clipboard"
          >
            <Copy className="h-3 w-3 text-gray-500" />
          </Button>
        )}
      </div>
    </div>
  );
}